// Admin optimization detail modal (v3).
//
// Renders the data-driven <SuggestionCard> for the selected suggestion. It is a
// drop-in replacement for OptimizationDetailModalV2's prop interface, so the
// `isAdmin ? V3 : OptimizationDetailModal` split keeps the non-admin legacy modal
// (OptimizationDetailModal) completely untouched.
//
// `optimization` / `allOptimizations` are suggestionToCard outputs; we read the
// raw v2 suggestion off `_raw` (full fields) and fall back to the card itself.
// Aplicar / Descartar build their request inside SuggestionCard and we execute it
// here via services/optimizationActions, then notify the page (onApplySuccess /
// onRatingSuccess) so it can update its lists.
import React, { useCallback, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import SuggestionCard from '../optimizations/SuggestionCard';
import { runApplyRequest, runDismissRequest } from '../../services/optimizationActions';

const navBtn =
  'min-h-[36px] inline-flex items-center justify-center rounded-lg border border-white/15 bg-white/5 px-2 text-white/80 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40';

export default function OptimizationDetailModalV3({
  isOpen,
  onClose,
  optimization,
  allOptimizations,
  currentIndex,
  onNavigate,
  onRatingSuccess,
  onApplySuccess,
}) {
  const list = Array.isArray(allOptimizations) ? allOptimizations : [];
  const total = list.length;
  const idx = Number.isInteger(currentIndex) ? currentIndex : 0;
  const card = optimization || list[idx] || null;
  const raw = card?._raw ?? card ?? null;

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  const goPrev = useCallback(() => { if (idx > 0) onNavigate?.(idx - 1); }, [idx, onNavigate]);
  const goNext = useCallback(() => { if (idx < total - 1) onNavigate?.(idx + 1); }, [idx, total, onNavigate]);

  const handleApply = useCallback((sug, req) => {
    runApplyRequest(req)
      .then(() => { toast.success('Optimización aplicada'); onApplySuccess?.(sug?.id, 'APPLIED'); })
      .catch(() => toast.error('No se pudo aplicar la optimización'));
  }, [onApplySuccess]);

  const handleDismiss = useCallback((sug, req) => {
    runDismissRequest(req)
      .then(() => { toast.success('Optimización descartada'); onRatingSuccess?.(sug?.id, false, { status: 'DISMISSED' }); })
      .catch(() => toast.error('No se pudo descartar la optimización'));
  }, [onRatingSuccess]);

  // "Entendido" on informational multi-item cards: acknowledge (mark seen) and clear it
  // from the list. There's no dedicated "seen" endpoint, so this persists via /dismiss
  // with a neutral rating (is_liked: null) rather than a negative one.
  const handleMarkSeen = useCallback((sug, req) => {
    runDismissRequest(req)
      .then(() => { toast.success('Marcada como vista'); onRatingSuccess?.(sug?.id, null, { status: 'DISMISSED' }); })
      .catch(() => toast.error('No se pudo actualizar la optimización'));
  }, [onRatingSuccess]);

  if (!isOpen || !raw) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-slate-950/80 backdrop-blur-sm p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="relative w-full max-w-3xl my-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {total > 1 ? (
              <>
                <button type="button" onClick={goPrev} disabled={idx <= 0} className={navBtn} aria-label="Anterior">
                  <ChevronLeft className="w-4 h-4" aria-hidden />
                </button>
                <span className="text-xs font-semibold text-white/60 tabular-nums">{idx + 1} / {total}</span>
                <button type="button" onClick={goNext} disabled={idx >= total - 1} className={navBtn} aria-label="Siguiente">
                  <ChevronRight className="w-4 h-4" aria-hidden />
                </button>
              </>
            ) : null}
          </div>
          <button type="button" onClick={onClose} className={navBtn} aria-label="Cerrar">
            <X className="w-4 h-4" aria-hidden />
          </button>
        </div>

        {/* key by suggestion id so per-card state (selectedIds, edited value) resets
            when navigating the carousel instead of leaking across suggestions. */}
        <SuggestionCard key={raw?.id ?? idx} suggestion={raw} onApply={handleApply} onDismiss={handleDismiss} onMarkSeen={handleMarkSeen} />
      </div>
    </div>
  );
}
